import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminDashboard, dashboardSummary, teamActivity, adminLlmCacheStats, adminLlmBudget, adminUpdateLlmBudget } from "@/lib/api";
import { Users, TrendingUp, Megaphone, Calendar, CheckSquare, Inbox, AlertCircle, CalendarCheck, MessageSquare, Sparkles, DollarSign, Pencil } from "lucide-react";
import DistrictHeatmap from "@/components/admin/DistrictHeatmap";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Stat = ({ label, value, icon: Icon, to, tint }) => {
  const inner = (
    <div className={`bg-white border p-6 h-full ${tint || "border-stone-200"}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">{label}</div>
          <div className="font-serif text-4xl text-ccc-plum">{value ?? "—"}</div>
        </div>
        <Icon size={20} className="text-ccc-purple"/>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState([]);
  const [llmCache, setLlmCache] = useState(null);
  const [budget, setBudget] = useState(null);
  const [editingBudget, setEditingBudget] = useState(false);

  const load = useCallback(() => {
    adminDashboard().then(setStats);
    dashboardSummary().then(setSummary);
    teamActivity({ limit: 15 }).then(setActivity);
    adminLlmCacheStats(7).then(setLlmCache).catch(() => {});
    adminLlmBudget().then(setBudget).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const gcalOn = summary?.gcal_configured;
  const isAdmin = user?.role === "Admin";

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-ccc-purple">Overview</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Dashboard.</h1>
        </div>
        <div className="text-xs text-stone-500">
          Google Calendar sync:{" "}
          <span className={gcalOn ? "text-emerald-600 font-semibold" : "text-stone-400"} data-testid="gcal-status-badge">
            {gcalOn ? "Live ✓" : "Not configured"}
          </span>
        </div>
      </div>

      {/* Row 1: Personal + Team KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Stat label="Unread Inbox"        value={summary?.unread_notifications}  icon={Inbox}         to="/admin/inbox"  tint={summary?.unread_notifications > 0 ? "border-ccc-purple/30" : "border-stone-200"}/>
        <Stat label="My Open Tasks"       value={summary?.my_open_tasks_count}   icon={CheckSquare}   to="/admin/tasks"/>
        <Stat label="Overdue Tasks"       value={summary?.overdue_tasks_count}   icon={AlertCircle}   to="/admin/tasks"  tint={summary?.overdue_tasks_count > 0 ? "border-red-300" : "border-stone-200"}/>
        <Stat label="Upcoming Events (7d)" value={summary?.upcoming_events?.length} icon={CalendarCheck} to="/admin/events"/>
      </div>

      {/* Row 1b: AI Assistant engagement tile */}
      <div className="mb-6">
        <Link to="/admin/conversations" data-testid="stat-conversations-today" className="block">
          <div className="bg-gradient-to-r from-ccc-plum to-ccc-purple text-white p-5 flex items-center justify-between hover:opacity-95 transition">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <MessageSquare size={18}/>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-ccc-gold">AI Assistant · Today</div>
                <div className="font-serif text-2xl">
                  {summary?.conversations_today ?? 0} conversation{summary?.conversations_today === 1 ? "" : "s"}
                  <span className="text-white/60 mx-2">·</span>
                  {summary?.leads_today ?? 0} lead{summary?.leads_today === 1 ? "" : "s"}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-ccc-gold">Conversion</div>
              <div className="font-serif text-3xl">{summary?.lead_conversion_rate ?? 0}<span className="text-lg opacity-70">%</span></div>
            </div>
          </div>
        </Link>
      </div>

      {/* Row 1c: LLM cache observability — small ROI tile */}
      {llmCache && llmCache.total_requests > 0 && (
        <div className="mb-6 bg-white border border-stone-200 p-4 flex items-center gap-4" data-testid="stat-llm-cache">
          <div className="w-10 h-10 rounded-full bg-ccc-gold/15 text-ccc-gold-dark flex items-center justify-center flex-shrink-0">
            <Sparkles size={18}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-ccc-purple">AI cache · past {llmCache.window_days}d</div>
            <div className="text-sm text-stone-700 mt-0.5">
              <span className="font-mono text-lg text-ccc-plum font-semibold" data-testid="cache-hit-rate">{llmCache.hit_rate}%</span>
              <span className="text-stone-500 mx-1.5">hit rate</span>
              <span className="text-stone-400">·</span>
              <span className="ml-1.5">{llmCache.hits} hit{llmCache.hits === 1 ? "" : "s"}, {llmCache.misses} miss{llmCache.misses === 1 ? "" : "es"}, {llmCache.refreshes} refresh{llmCache.refreshes === 1 ? "" : "es"}</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[11px] uppercase tracking-widest text-ccc-gold-dark">Est. LLM savings</div>
            <div className="font-serif text-2xl text-ccc-plum" data-testid="cache-savings">
              ≈${llmCache.estimated_savings_usd.toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Row 1d: AI budget — MTD spend vs monthly cap */}
      {budget && <BudgetCard budget={budget} isAdmin={isAdmin} onEdit={() => setEditingBudget(true)}/>}

      {/* Row 2: Two-column layout - Tasks & Notifications | Activity */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {/* My Tasks Today */}
        <div className="bg-white border border-stone-200 p-6" data-testid="widget-my-tasks">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-xl text-ccc-plum">My Tasks</h2>
            <Link to="/admin/tasks" className="text-xs text-ccc-purple uppercase tracking-wider">View all</Link>
          </div>
          {summary?.my_open_tasks?.length ? (
            <ul className="space-y-2">
              {summary.my_open_tasks.slice(0, 6).map(t => (
                <li key={t.id} className="text-sm border-b border-stone-100 pb-2 last:border-b-0">
                  <div className="font-medium text-ccc-plum">{t.title}</div>
                  <div className="text-xs text-stone-500">
                    {t.due_date ? `Due ${t.due_date}` : "No due date"} · {t.priority}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-stone-500 italic">Nothing on your plate. 🕊️</div>
          )}
        </div>

        {/* Upcoming Events */}
        <div className="bg-white border border-stone-200 p-6" data-testid="widget-upcoming-events">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-xl text-ccc-plum">Upcoming Events</h2>
            <Link to="/admin/events" className="text-xs text-ccc-purple uppercase tracking-wider">View all</Link>
          </div>
          {summary?.upcoming_events?.length ? (
            <ul className="space-y-2">
              {summary.upcoming_events.slice(0, 6).map(e => (
                <li key={e.id} className="text-sm border-b border-stone-100 pb-2 last:border-b-0">
                  <div className="font-medium text-ccc-plum">{e.title}</div>
                  <div className="text-xs text-stone-500">
                    {new Date(e.starts_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    {e.location ? ` · ${e.location}` : ""}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-stone-500 italic">No events in the next 7 days.</div>
          )}
        </div>

        {/* Recent Notifications */}
        <div className="bg-white border border-stone-200 p-6" data-testid="widget-recent-notifications">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-xl text-ccc-plum">Recent Signals</h2>
            <Link to="/admin/inbox" className="text-xs text-ccc-purple uppercase tracking-wider">Open inbox</Link>
          </div>
          {summary?.recent_notifications?.length ? (
            <ul className="space-y-2">
              {summary.recent_notifications.slice(0, 6).map(n => (
                <li key={n.id} className="text-sm border-b border-stone-100 pb-2 last:border-b-0">
                  <div className="font-medium text-ccc-plum truncate">{n.title}</div>
                  <div className="text-xs text-stone-500">{n.subtitle}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-stone-500 italic">Silence for now.</div>
          )}
        </div>
      </div>

      {/* Row 3: Original stats (preserved) */}
      <h2 className="font-serif text-2xl text-ccc-plum mb-3">The advocacy pulse</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Contacts" value={stats?.total_contacts} icon={Users} to="/admin/contacts"/>
        <Stat label="New (30 days)" value={stats?.new_contacts_30d} icon={TrendingUp}/>
        <Stat label="Active Alerts" value={stats?.active_alerts} icon={Megaphone}/>
        <Stat label="Upcoming Events" value={stats?.upcoming_events} icon={Calendar}/>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-stone-200 p-6">
          <h2 className="font-serif text-2xl text-ccc-plum mb-4">Bills by position</h2>
          <div className="space-y-3">
            {stats?.bills_by_position && Object.entries(stats.bills_by_position).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="w-24 text-sm text-stone-600">{k}</span>
                <div className="flex-1 h-3 bg-stone-100">
                  <div className="h-full bg-ccc-purple" style={{ width: `${Math.min(100, v * 20)}%` }}/>
                </div>
                <span className="w-8 text-right text-sm">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-stone-200 p-6" data-testid="widget-team-activity">
          <h2 className="font-serif text-2xl text-ccc-plum mb-4">Team Activity</h2>
          {activity.length ? (
            <ul className="space-y-2 max-h-72 overflow-y-auto">
              {activity.map(a => {
                let when = a.created_at;
                try { when = formatDistanceToNow(new Date(a.created_at), { addSuffix: true }); } catch { /* ignore */ }
                return (
                  <li key={a.id} className="text-sm border-b border-stone-100 pb-2 last:border-b-0">
                    <div className="flex justify-between text-xs text-stone-500">
                      <span className="uppercase tracking-wider">{a.type}</span>
                      <span>{when}</span>
                    </div>
                    <div className="text-stone-800">{a.detail || a.type}</div>
                    {a.actor && <div className="text-xs text-stone-500">by {a.actor}</div>}
                  </li>
                );
              })}
            </ul>
          ) : <div className="text-sm text-stone-500">Activity will appear here as the team works.</div>}
        </div>
      </div>

      <div className="mt-4">
        <DistrictHeatmap/>
      </div>

      <div className="mt-8 bg-ccc-plum text-ccc-ivory p-8">
        <div className="text-ccc-gold uppercase tracking-[0.3em] text-xs mb-2">This session at a glance</div>
        <div className="font-serif text-3xl">{stats?.actions_this_session ?? 0} advocacy actions taken.</div>
      </div>
      <EditBudgetDialog
        open={editingBudget}
        onOpenChange={setEditingBudget}
        current={budget}
        onSaved={(fresh) => { setBudget(fresh); setEditingBudget(false); toast.success("Budget updated"); }}
      />
    </div>
  );
}

/* --- AI Budget subcomponents ------------------------------------------- */

function BudgetCard({ budget, isAdmin, onEdit }) {
  const pct = Math.min(100, budget.pct_used);
  const status = budget.status;
  const barColor =
    status === "over" ? "bg-red-500"
    : status === "warn" ? "bg-amber-500"
    : "bg-ccc-purple";
  const dot =
    status === "over" ? "bg-red-500"
    : status === "warn" ? "bg-amber-500"
    : "bg-emerald-500";
  const label =
    status === "over" ? "Over budget"
    : status === "warn" ? `${Math.round(budget.pct_used)}% used — warn threshold crossed`
    : "On track";
  return (
    <div className="mb-6 bg-white border border-stone-200 p-4" data-testid="stat-llm-budget">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-ccc-plum/10 text-ccc-plum flex items-center justify-center flex-shrink-0">
          <DollarSign size={18}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-ccc-purple flex items-center gap-2">
            AI budget · {budget.month_key}
            <span className={`inline-block w-2 h-2 rounded-full ${dot}`} data-testid="budget-status-dot"/>
            <span className="text-stone-500 normal-case tracking-normal text-xs" data-testid="budget-status-label">{label}</span>
          </div>
          <div className="text-sm text-stone-700 mt-0.5" data-testid="budget-spend-line">
            <span className="font-mono text-lg text-ccc-plum font-semibold">${budget.month_to_date_usd.toFixed(2)}</span>
            <span className="text-stone-500"> of ${budget.monthly_cap_usd.toFixed(2)}</span>
            <span className="text-stone-400 mx-1.5">·</span>
            <span className="text-stone-500">warn at {budget.warn_pct}%</span>
            <span className="text-stone-400 mx-1.5">·</span>
            <span className="text-stone-500">{budget.call_count} call{budget.call_count === 1 ? "" : "s"}</span>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={onEdit}
            className="text-xs text-ccc-purple uppercase tracking-wider hover:underline flex items-center gap-1"
            data-testid="budget-edit-btn"
          >
            <Pencil size={12}/> Edit cap
          </button>
        )}
      </div>
      <div className="mt-3 h-2 bg-stone-100 rounded-sm overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${pct}%` }}
          data-testid="budget-progress-bar"
        />
      </div>
    </div>
  );
}

function EditBudgetDialog({ open, onOpenChange, current, onSaved }) {
  const [cap, setCap] = useState("");
  const [warn, setWarn] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && current) {
      setCap(String(current.monthly_cap_usd));
      setWarn(String(current.warn_pct));
    }
  }, [open, current]);

  const save = async () => {
    const capF = parseFloat(cap);
    const warnI = parseInt(warn, 10);
    if (!Number.isFinite(capF) || capF < 0 || capF > 10000) {
      toast.error("Cap must be between $0 and $10,000");
      return;
    }
    if (!Number.isFinite(warnI) || warnI < 1 || warnI > 100) {
      toast.error("Warn threshold must be 1–100");
      return;
    }
    setSaving(true);
    try {
      const fresh = await adminUpdateLlmBudget({ monthly_cap_usd: capF, warn_pct: warnI });
      onSaved(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update budget");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="budget-edit-dialog">
        <DialogHeader>
          <DialogTitle>AI budget settings</DialogTitle>
          <DialogDescription>
            Set a monthly Emergent LLM key spend ceiling and the % that triggers a red banner + admin email.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="budget-cap">Monthly cap (USD)</Label>
            <Input
              id="budget-cap"
              type="number" min="0" max="10000" step="0.01"
              value={cap} onChange={(e) => setCap(e.target.value)}
              data-testid="budget-cap-input"
            />
            <p className="text-xs text-stone-500 mt-1">Emergent LLM key spend. Rough — $0.01–0.02 per LLM call.</p>
          </div>
          <div>
            <Label htmlFor="budget-warn">Warn threshold (%)</Label>
            <Input
              id="budget-warn"
              type="number" min="1" max="100" step="1"
              value={warn} onChange={(e) => setWarn(e.target.value)}
              data-testid="budget-warn-input"
            />
            <p className="text-xs text-stone-500 mt-1">
              Email admins + show a red banner when MTD spend exceeds this %.
            </p>
          </div>
          <div className="text-xs text-stone-500 border-t pt-3">
            {current?.updated_by && current.updated_by !== "default" ? (
              <>Last updated by <span className="font-medium text-stone-700">{current.updated_by}</span> · {current.updated_at ? new Date(current.updated_at).toLocaleString() : ""}</>
            ) : (
              <>Using defaults — never changed by any admin.</>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="budget-cancel-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="budget-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
